import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Link, Dialog, DialogTitle,
  DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox, useTheme
} from '@mui/material';
import { useCookieConsent } from '../consent/CookieConsentProvider';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Баннер появляется, если статус 'unknown'.
 * Расположен над fixed-footer, чтобы не перекрываться.
 */
const FOOTER_OFFSET = 80; // px; подгон под высоту твоего футера

const CookieBanner = () => {
  const theme = useTheme();
  const { status, categories, acceptAll, rejectAll, saveCustom, __attachSettingsOpener } = useCookieConsent();
  const [openSettings, setOpenSettings] = useState(false);
  const [localAnalytics, setLocalAnalytics] = useState(categories.analytics);

  // позволяем открывать настройки из футера
  useEffect(() => {
    __attachSettingsOpener(() => {
      setLocalAnalytics(categories.analytics);
      setOpenSettings(true);
    });
  }, [__attachSettingsOpener, categories.analytics]);

  const handleSave = () => {
    saveCustom({ analytics: localAnalytics });
    setOpenSettings(false);
  };

  if (status !== 'unknown') return null;

  return (
    <>
      <Paper
        elevation={8}
        role="dialog"
        aria-label="Согласие на использование файлов cookie"
        sx={{
          position: 'fixed',
          left: { xs: 8, sm: 16 },
          right: { xs: 8, sm: 16 },
          bottom: { xs: FOOTER_OFFSET, sm: FOOTER_OFFSET },
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          zIndex: (t) => t.zIndex.snackbar + 1,
          backgroundColor: (t) => t.palette.background.paper,
          border: (t) =>
            `1px solid ${t.palette.mode === 'dark'
              ? 'rgba(148,163,184,0.20)' : 'rgba(15,23,42,0.08)'}`
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box sx={{ pr: { md: 2 } }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Мы используем файлы cookie
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Необходимые — для работы сайта. Аналитические помогают улучшать сервис. Подробнее в{' '}
              <Link component={RouterLink} to="/privacy" underline="hover">Политике конфиденциальности</Link>{' '}
              и <Link component={RouterLink} to="/terms" underline="hover">Пользовательском соглашении</Link>.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={() => setOpenSettings(true)}>
              Настройки
            </Button>
            <Button variant="text" onClick={rejectAll}>
              Отклонить
            </Button>
            <Button variant="contained" onClick={acceptAll} sx={{ backgroundColor: theme.palette.primary.main }}>
              Принять все
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={openSettings} onClose={() => setOpenSettings(false)} fullWidth maxWidth="sm">
        <DialogTitle>Настройки cookie</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Необходимые</Typography>
              <Typography variant="body2" color="text.secondary">
                Всегда активны — обеспечивают безопасность, авторизацию и базовые функции сайта.
              </Typography>
            </Box>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked
                    disabled
                  />
                }
                label={<Typography variant="body2">Обязательные cookie</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localAnalytics}
                    onChange={(e) => setLocalAnalytics(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Аналитические cookie (улучшение качества сервиса)</Typography>}
              />
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSettings(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSave}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieBanner;
